import type { HelpdeskRequestBucket, HelpdeskRequestRelationshipFilter, HelpdeskUnreadCount } from "@repo/shared-types/helpdesk";
import { useQuery } from "@tanstack/react-query";

export const UNREAD_COUNT_REFRESH_MS = 60_000;

type UnreadCountFilter = { bucket: HelpdeskRequestBucket; relationship?: HelpdeskRequestRelationshipFilter };

export const unreadCountKey = (scope: readonly unknown[], filter?: UnreadCountFilter): readonly unknown[] =>
  filter ? [...scope, "unread-count", filter.bucket, filter.relationship ?? "all"] : [...scope, "unread-count"];

export function useUnreadRequestCount(load: () => Promise<HelpdeskUnreadCount>, scope: readonly unknown[], enabled = true, filter?: UnreadCountFilter): HelpdeskUnreadCount | null {
  const query = useQuery({
    queryKey: unreadCountKey(scope, filter),
    queryFn: () => load(),
    enabled,
    refetchInterval: () => (document.visibilityState === "visible" ? UNREAD_COUNT_REFRESH_MS : false),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: false,
  });
  // A failed refresh must not keep claiming unread replies.
  return query.isError ? null : (query.data ?? null);
}

const countLabel = (count: HelpdeskUnreadCount): string => (count.capped ? "99+" : String(count.count));

export function NavCount({ count }: { count: HelpdeskUnreadCount | null }) {
  if (!count || count.count === 0) return null;
  const label = countLabel(count);
  return (
    <>
      <span className="nav-count" aria-hidden="true">
        {label}
      </span>
      <span className="sr-only">, {label} unread</span>
    </>
  );
}

export function titleCount(count: HelpdeskUnreadCount | null): number | undefined {
  return count && count.count > 0 ? count.count : undefined;
}
